
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import AIModelConfig from "@/components/admin/AIModelConfig";
import PromptTestingLab from "@/components/admin/PromptTestingLab";
import TestCallRunner from "@/components/admin/TestCallRunner";
import { AgentManagement } from "@/components/admin/AgentManagement";
import ConversationsList from "@/components/admin/ConversationalAI/ConversationsList";
import SentryErrorTest from "@/components/admin/SentryErrorTest";
import { ArrowLeft } from "lucide-react";
import { SupabaseConversationsList } from "@/components/admin/ConversationalAI/SupabaseConversationsList";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("ai-model");

  return (
    <div className="container max-w-7xl mx-auto py-6">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => navigate("/dashboard")}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Admin AI Ops Dashboard</h1>
              <p className="text-muted-foreground">
                Configure AI models, test prompts, and manage agents
              </p>
            </div>
          </div>
          <SentryErrorTest />
        </div>

        <Tabs
          defaultValue="ai-model"
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="grid grid-cols-6 gap-2">
            <TabsTrigger value="ai-model">AI Model Config</TabsTrigger>
            <TabsTrigger value="prompt-testing">Prompt Testing</TabsTrigger>
            <TabsTrigger value="agent-management">Agent Manager</TabsTrigger>
            <TabsTrigger value="test-call">Test Call Runner</TabsTrigger>
            <TabsTrigger value="conversations">ElevenLabs</TabsTrigger>
            <TabsTrigger value="supabase-conversations">Supabase Data</TabsTrigger>
          </TabsList>

          <TabsContent value="ai-model" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI Model Configuration</CardTitle>
                <CardDescription>
                  Select model provider and tweak LLM settings per task
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AIModelConfig />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="prompt-testing" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Prompt Testing Lab</CardTitle>
                <CardDescription>
                  Test how the AI personalizes and interprets calls
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PromptTestingLab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="agent-management" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>AI Agent Manager</CardTitle>
                <CardDescription>
                  Manage ElevenLabs conversational AI agents and their variables
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 pt-6 pb-6">
                <AgentManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="test-call" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Test Call Runner</CardTitle>
                <CardDescription>
                  Send a test call using a selected agent to your phone
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TestCallRunner />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="conversations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Conversations (ElevenLabs API)</CardTitle>
                <CardDescription>
                  Browse and manage all AI agent conversations and their linked leads
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConversationsList />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="supabase-conversations" className="space-y-4">
            <SupabaseConversationsList />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
